import React, { useState, useMemo } from 'react';
import { CitationAnchor } from '../types/api';
import { CitationHighlight } from './CitationHighlight';
import { CitationSourcePanel } from './CitationSourcePanel';

interface AnswerWithCitationsProps {
  answer: string;
  citations: CitationAnchor[];
  opinions?: { id: string; polarity?: string }[];
}

/**
 * Complete answer display component with citation highlighting and source panel.
 * Usage:
 * 
 * <AnswerWithCitations 
 *   answer={response.answer} 
 *   citations={response.citations} 
 * />
 */
export const AnswerWithCitations: React.FC<AnswerWithCitationsProps> = ({
  answer,
  citations,
  opinions
}) => {
  const [selectedCitation, setSelectedCitation] = useState<CitationAnchor | null>(null);

  // Group citations by document ID and chunk text
  const citationsBySource = useMemo(() => {
    const grouped = new Map<string, CitationAnchor[]>();
    citations.forEach((citation, index) => {
      const key = `${citation.documentId}:${citation.chunkText}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      // Add citation index for numbered badges
      grouped.get(key)!.push({ ...citation, citationIndex: index + 1 } as any);
    });
    return grouped;
  }, [citations]);

  // Get all citations for the selected source
  const selectedSourceCitations = useMemo(() => {
    if (!selectedCitation) return [];
    const key = `${selectedCitation.documentId}:${selectedCitation.chunkText}`;
    return citationsBySource.get(key) || [];
  }, [selectedCitation, citationsBySource]);

  return (
    <div data-component="AnswerWithCitations" style={{ position: 'relative' }}>
      <CitationHighlight
        answer={answer}
        citations={citations}
        onCitationClick={setSelectedCitation}
      />

      {selectedCitation && (
        <CitationSourcePanel
          citations={selectedSourceCitations}
          polarity={opinions?.find(o => o.id === selectedCitation.documentId)?.polarity}
          onClose={() => setSelectedCitation(null)}
        />
      )}
    </div>
  );
};
